import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { GroupSettings } from '../types';

interface GroupInfoCardProps {
  groupSettings: GroupSettings;
  onUpdate: (settings: Partial<GroupSettings>) => void;
}

const GroupInfoCard = ({ groupSettings, onUpdate }: GroupInfoCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: groupSettings.name,
    description: groupSettings.description,
    membershipFee: groupSettings.membershipFee?.toString() || '0',
  });
  const [errors, setErrors] = useState<{ name?: string; description?: string; membershipFee?: string }>({});


  const handleSave = () => {
    const newErrors: { name?: string; description?: string; membershipFee?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    const fee = parseFloat(formData.membershipFee);
    if (isNaN(fee) || fee < 0) {
      newErrors.membershipFee = 'Please enter a valid membership fee';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate({
      ...formData,
      membershipFee: fee,
    });
    setIsEditing(false);
    setErrors({});
  };

  const handleCancel = () => {
    setFormData({
      name: groupSettings.name,
      description: groupSettings.description,
      membershipFee: groupSettings.membershipFee?.toString() || '0',
    });
    setIsEditing(false);
    setErrors({});
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {groupSettings.companyLogoUrl ? (
            <img
              src={groupSettings.companyLogoUrl}
              alt={groupSettings.name}
              className="w-12 h-12 rounded-md object-cover border border-border"
            />
          ) : (
            <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
              <Icon name="Users" size={24} color="var(--color-primary)" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold text-foreground">{groupSettings.name}</h2>
            {groupSettings.companyName && (
              <p className="text-sm text-muted-foreground">{groupSettings.companyName}</p>
            )}
            {!groupSettings.companyName && (
              <p className="text-sm text-muted-foreground">
                Manage your group's basic details
              </p>
            )}
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {isEditing ? (
          <>
            <Input
              label="Group Name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={errors.name}
              required
              placeholder="Enter group name"
            />
            <Input
              label="Description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              error={errors.description}
              required
              placeholder="Enter group description"
            />
            <Input
              label="Membership Fee (One-time)"
              type="number"
              value={formData.membershipFee}
              onChange={(e) => setFormData({ ...formData, membershipFee: e.target.value })}
              error={errors.membershipFee}
              placeholder="0.00"
              suffix="VT"
            />
            <div className="flex gap-3 pt-2">
              <Button variant="default" onClick={handleSave}>
                Save Changes
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Group Name</label>
              <p className="text-base text-foreground mt-1">{groupSettings.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="text-base text-foreground mt-1">{groupSettings.description}</p>
            </div>
            {(groupSettings.companyName || groupSettings.companyLogoUrl) && (
              <div className="pt-4 border-t border-border">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Company/Organization</label>
                <div className="flex items-center gap-4">
                  {groupSettings.companyLogoUrl && (
                    <img
                      src={groupSettings.companyLogoUrl}
                      alt="Company logo"
                      className="h-16 w-16 object-contain border border-border rounded-md p-2 bg-muted/50"
                    />
                  )}
                  {groupSettings.companyName && (
                    <p className="text-base font-semibold text-foreground">{groupSettings.companyName}</p>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-border">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Members</label>
                <p className="text-2xl font-semibold text-foreground mt-1">
                  {groupSettings.memberCount}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Total Contributions
                </label>
                <p className="text-2xl font-semibold text-foreground mt-1 text-emerald-600">
                  {groupSettings.totalContributions.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} VT
                </p>
              </div>
              {groupSettings.moneyInHand !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Cash in hand
                  </label>
                  <p className="text-2xl font-semibold text-foreground mt-1 text-green-600">
                    {groupSettings.moneyInHand.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} VT
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Membership Fee
                </label>
                <p className="text-2xl font-semibold text-foreground mt-1">
                  {(groupSettings.membershipFee || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} VT
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created</label>
                <p className="text-2xl font-semibold text-foreground mt-1">
                  {groupSettings.createdAt.toLocaleDateString()}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupInfoCard;
